/**
 * Profile — the client-facing monitoring profile editor.
 *
 * Edits the same `profiles` / `keywords` D1 tables the Chrome extension
 * reads via /api/config/snapshot, so changes here propagate to the
 * extension (within its 60s sync) and show up on the agency dashboard too.
 *
 * Phase 2a (this file): business-detail fields + positive/negative keyword
 * editing. Phase 2b will add AI training + watchlist management.
 *
 * Scope: a `client` edits their own account automatically. An owner / VA
 * picks a client from the dropdown (reuses the /admin/clients list).
 */
import { useCallback, useEffect, useState } from 'react';

import { AppShell } from '../components/AppShell';
import {
  ApiError,
  useApi,
  type MeResponse,
  type MeConfigResponse,
  type ConfigProfile,
} from '../lib/api';

// Business-detail form shape — kept module-level so <ProfileField> below can
// be a stable component (recreating it inside ProfileEditor each render is
// what caused the inputs to lose focus on every keystroke).
type ProfileForm = {
  name: string;
  industry: string;
  value_proposition: string;
  promotion: string;
  tone: string;
  match_mode: string;
};

// A single labelled input/textarea. Hoisted out of ProfileEditor so its
// identity is stable across renders → controlled inputs keep focus.
function ProfileField({
  label,
  value,
  onChange,
  textarea,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  textarea?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="profile-field">
      <span className="profile-field-label">{label}</span>
      {textarea ? (
        <textarea
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
        />
      ) : (
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}

// A card whose body collapses behind a clickable header + chevron. Used for
// every section of the Profile editor so the page reads as a compact stack
// of expandable rows. Defaults to expanded.
function CollapsibleCard({
  title,
  subtitle,
  defaultOpen = true,
  children,
}: {
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="card collapsible-card">
      <button
        type="button"
        className="collapsible-head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="collapsible-chevron" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span className="collapsible-titles">
          <span className="collapsible-title">{title}</span>
          {subtitle && (
            <span className="collapsible-subtitle">{subtitle}</span>
          )}
        </span>
      </button>
      {/* Keep the body mounted and hide with CSS rather than unmounting it —
          unmounting would discard unsaved edits in child editors (Watchlist /
          AI Training hold their own form state) when a section is collapsed. */}
      <div className="collapsible-body" hidden={!open}>
        {children}
      </div>
    </section>
  );
}

type ClientPick = { id: string; name: string };

export function Profile() {
  const { request } = useApi();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [clients, setClients] = useState<ClientPick[] | null>(null);
  const [pickedClient, setPickedClient] = useState<string>('');

  const [config, setConfig] = useState<MeConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    request<MeResponse>('/api/me')
      .then(setMe)
      .catch(() => setMe(null));
  }, [request]);

  // Owners / VAs need a client picker. Pull the client list once we know
  // the role isn't `client`.
  useEffect(() => {
    if (me && !me.user_not_linked && me.user.role !== 'client') {
      request<{ clients: { id: string; name: string }[] }>('/admin/clients')
        .then((res) => {
          setClients(res.clients.map((c) => ({ id: c.id, name: c.name })));
          if (res.clients[0]) {
            setPickedClient(res.clients[0].id);
          } else {
            // Owner/VA with no clients — loadConfig will never fire (it waits
            // on pickedClient), so clear the spinner ourselves and let the
            // empty-state render.
            setLoading(false);
          }
        })
        .catch(() => {
          setClients([]);
          setLoading(false);
        });
    }
  }, [me, request]);

  const role = me && !me.user_not_linked ? me.user.role : undefined;
  const isClient = role === 'client';

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const qs =
        !isClient && pickedClient
          ? `?client_account_id=${encodeURIComponent(pickedClient)}`
          : '';
      const res = await request<MeConfigResponse>(`/api/me/config${qs}`);
      setConfig(res);
    } catch (e) {
      setErr((e as Error).message);
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [request, isClient, pickedClient]);

  // Load config once we know the role. For owners/VAs wait for a picked client.
  useEffect(() => {
    if (!me || me.user_not_linked) return;
    if (isClient || pickedClient) void loadConfig();
  }, [me, isClient, pickedClient, loadConfig]);

  const userRole = role;

  return (
    <AppShell {...(userRole ? { userRole } : {})}>
      <main className="page">
        <div className="page-hero">
          <div className="page-hero-icon" aria-hidden="true">🧭</div>
          <div className="page-hero-text">
            <h1 className="page-hero-title">Profile</h1>
            <p className="page-hero-sub">
              Your monitoring profile — business details and keywords.
              Changes sync to the Chrome extension and the dashboards.
            </p>
          </div>
        </div>

        {/* Owner / VA client picker */}
        {!isClient && clients && clients.length > 0 && (
          <div className="leads-filter-bar">
            <label
              htmlFor="profile-client-picker"
              style={{ fontSize: 13, fontWeight: 500, color: 'var(--muted)' }}
            >
              Editing profile for
            </label>
            <select
              id="profile-client-picker"
              value={pickedClient}
              onChange={(e) => setPickedClient(e.target.value)}
              className="leads-filter-select"
            >
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Owner/VA with no clients yet — distinct from "loading". */}
        {!isClient && clients && clients.length === 0 ? (
          <section className="card">
            <h2>No Clients Yet</h2>
            <p className="muted" style={{ marginBottom: 0 }}>
              Once you onboard a client, their monitoring profile will be
              editable here.
            </p>
          </section>
        ) : loading ? (
          <section className="card">
            <p className="muted" style={{ margin: 0 }}>Loading profile…</p>
          </section>
        ) : err ? (
          <section className="card">
            <p style={{ color: '#b91c1c', margin: 0 }}>{err}</p>
          </section>
        ) : !config || config.profiles.length === 0 ? (
          <section className="card">
            <h2>No Profile Yet</h2>
            <p className="muted" style={{ marginBottom: 0 }}>
              {isClient
                ? 'Your account doesn’t have a monitoring profile yet. Ask your agency to set one up.'
                : 'This client has no profile yet.'}
            </p>
          </section>
        ) : (
          <ProfileEditor
            profile={config.profiles[0]!}
            keywords={config.keywords}
            watchedGroups={config.watched_groups}
            clientAccountId={config.client_account_id}
            onReload={loadConfig}
          />
        )}
      </main>
    </AppShell>
  );
}

// -----------------------------------------------------------------------------
// ProfileEditor — business-detail form + keyword lists for a single profile.
// -----------------------------------------------------------------------------
function ProfileEditor({
  profile,
  keywords,
  watchedGroups,
  clientAccountId,
  onReload,
}: {
  profile: ConfigProfile;
  keywords: MeConfigResponse['keywords'];
  watchedGroups: MeConfigResponse['watched_groups'];
  clientAccountId: string;
  onReload: () => Promise<void>;
}) {
  const { request } = useApi();

  // --- business-detail form -------------------------------------------------
  const seedForm = (p: ConfigProfile): ProfileForm => ({
    name: p.name ?? '',
    industry: p.industry ?? '',
    value_proposition: p.value_proposition ?? '',
    promotion: p.promotion ?? '',
    tone: p.tone ?? '',
    match_mode: p.match_mode ?? 'includes',
  });
  const [form, setForm] = useState<ProfileForm>(() => seedForm(profile));
  const [savingFields, setSavingFields] = useState(false);
  const [fieldsMsg, setFieldsMsg] = useState<string | null>(null);

  // Re-seed the form ONLY when we switch to a different profile (owner picked
  // another client). Keying on profile.id — not the profile object identity —
  // means a config refetch after a keyword add/remove won't wipe unsaved
  // edits in this form.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setForm(seedForm(profile));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id]);

  async function saveFields() {
    setSavingFields(true);
    setFieldsMsg(null);
    try {
      await request(`/api/me/config/profile/${profile.id}`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      setFieldsMsg('Saved ✓ — syncing to the extension within 60s.');
      window.setTimeout(() => setFieldsMsg(null), 4000);
      // Refresh the parent config so other readers (and a later client
      // switch-and-back) see the saved values. Safe now that the re-seed
      // effect only fires on profile.id change.
      await onReload();
    } catch (e) {
      setFieldsMsg('Save failed: ' + (e as Error).message);
    } finally {
      setSavingFields(false);
    }
  }

  // --- keyword editing ------------------------------------------------------
  const positive = keywords.filter((k) => k.kind === 'positive');
  const negative = keywords.filter((k) => k.kind === 'negative');
  const [newPos, setNewPos] = useState('');
  const [newNeg, setNewNeg] = useState('');
  const [kwBusy, setKwBusy] = useState(false);
  const [kwMsg, setKwMsg] = useState<string | null>(null);

  async function addKeyword(kind: 'positive' | 'negative', value: string) {
    const keyword = value.trim();
    if (!keyword) return;
    setKwBusy(true);
    setKwMsg(null);
    try {
      await request('/api/me/config/keywords', {
        method: 'POST',
        body: JSON.stringify({ profile_id: profile.id, keyword, kind }),
      });
      if (kind === 'positive') setNewPos('');
      else setNewNeg('');
      await onReload();
    } catch (e) {
      // The API returns 409 for a duplicate (UNIQUE profile_id+keyword+kind).
      const isDup = e instanceof ApiError && e.status === 409;
      setKwMsg(
        isDup
          ? `"${keyword}" is already in the list.`
          : 'Add failed: ' + (e as Error).message,
      );
    } finally {
      setKwBusy(false);
    }
  }

  async function removeKeyword(id: string) {
    setKwBusy(true);
    setKwMsg(null);
    try {
      await request(`/api/me/config/keywords/${id}`, { method: 'DELETE' });
      await onReload();
    } catch (e) {
      setKwMsg('Remove failed: ' + (e as Error).message);
    } finally {
      setKwBusy(false);
    }
  }

  // Thin wrapper binding a ProfileForm key to the hoisted <ProfileField>.
  const bindField = (
    label: string,
    key: keyof ProfileForm,
    opts: { textarea?: boolean; placeholder?: string } = {},
  ) => (
    <ProfileField
      label={label}
      value={form[key]}
      onChange={(v) => setForm((f) => ({ ...f, [key]: v }))}
      {...(opts.textarea ? { textarea: true } : {})}
      {...(opts.placeholder ? { placeholder: opts.placeholder } : {})}
    />
  );

  return (
    <>
      {/* Business details */}
      <CollapsibleCard title="Business Details">
        <p className="muted" style={{ marginBottom: 16 }}>
          These shape how the AI analyzes posts and writes suggested replies.
        </p>
        <div className="profile-form">
          {bindField('Profile name', 'name', { placeholder: 'e.g. NEXA Lending' })}
          {bindField('Industry', 'industry', {
            placeholder: 'e.g. Real estate lending',
          })}
          {bindField('Value proposition', 'value_proposition', {
            textarea: true,
            placeholder: 'What you help people achieve, in one or two sentences.',
          })}
          {bindField('What you promote', 'promotion', {
            textarea: true,
            placeholder: 'The specific offer/service to weave into promo replies.',
          })}
          {bindField('Tone', 'tone', {
            placeholder: 'e.g. professional, warm, and direct',
          })}
          <label className="profile-field">
            <span className="profile-field-label">Keyword match mode</span>
            <select
              value={form.match_mode}
              onChange={(e) =>
                setForm((f) => ({ ...f, match_mode: e.target.value }))
              }
            >
              <option value="includes">
                Includes — substring match (broader, more leads)
              </option>
              <option value="exact">
                Exact — whole-word match (tighter, less noise)
              </option>
            </select>
          </label>
        </div>
        <div className="actions" style={{ justifyContent: 'flex-start', marginTop: 16 }}>
          <button
            className="btn btn-primary"
            onClick={() => void saveFields()}
            disabled={savingFields}
          >
            {savingFields ? 'Saving…' : 'Save Business Details'}
          </button>
          {fieldsMsg && (
            <span
              style={{
                fontSize: 13,
                color: fieldsMsg.startsWith('Save failed') ? '#b91c1c' : '#15803d',
                alignSelf: 'center',
              }}
            >
              {fieldsMsg}
            </span>
          )}
        </div>
      </CollapsibleCard>

      {/* Keywords */}
      <CollapsibleCard title="Keywords">
        <p className="muted" style={{ marginBottom: 16 }}>
          GroupPulse captures posts that contain a <strong>positive</strong>{' '}
          keyword. A <strong>negative</strong> keyword vetoes a post even if a
          positive one matched — use it to filter out sellers, spam, and noise.
        </p>

        <div className="kw-columns">
          {/* Positive */}
          <div className="kw-col">
            <div className="kw-col-head kw-col-head-pos">
              Positive keywords <span className="kw-count">{positive.length}</span>
            </div>
            <div className="kw-add-row">
              <input
                type="text"
                value={newPos}
                placeholder="Add a keyword…"
                onChange={(e) => setNewPos(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void addKeyword('positive', newPos);
                }}
                disabled={kwBusy}
              />
              <button
                className="btn btn-secondary"
                onClick={() => void addKeyword('positive', newPos)}
                disabled={kwBusy || !newPos.trim()}
              >
                Add
              </button>
            </div>
            <div className="kw-chips">
              {positive.length === 0 ? (
                <span className="muted" style={{ fontSize: 13, fontStyle: 'italic' }}>
                  No positive keywords yet — add at least one to start capturing.
                </span>
              ) : (
                positive.map((k) => (
                  <span key={k.id} className="kw-chip kw-chip-pos">
                    {k.keyword}
                    <button
                      type="button"
                      aria-label={`Remove ${k.keyword}`}
                      onClick={() => void removeKeyword(k.id)}
                      disabled={kwBusy}
                    >
                      ✕
                    </button>
                  </span>
                ))
              )}
            </div>
          </div>

          {/* Negative */}
          <div className="kw-col">
            <div className="kw-col-head kw-col-head-neg">
              Negative keywords <span className="kw-count">{negative.length}</span>
            </div>
            <div className="kw-add-row">
              <input
                type="text"
                value={newNeg}
                placeholder="Add a keyword to exclude…"
                onChange={(e) => setNewNeg(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void addKeyword('negative', newNeg);
                }}
                disabled={kwBusy}
              />
              <button
                className="btn btn-secondary"
                onClick={() => void addKeyword('negative', newNeg)}
                disabled={kwBusy || !newNeg.trim()}
              >
                Add
              </button>
            </div>
            <div className="kw-chips">
              {negative.length === 0 ? (
                <span className="muted" style={{ fontSize: 13, fontStyle: 'italic' }}>
                  No negative keywords.
                </span>
              ) : (
                negative.map((k) => (
                  <span key={k.id} className="kw-chip kw-chip-neg">
                    {k.keyword}
                    <button
                      type="button"
                      aria-label={`Remove ${k.keyword}`}
                      onClick={() => void removeKeyword(k.id)}
                      disabled={kwBusy}
                    >
                      ✕
                    </button>
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
        {kwMsg && (
          <div style={{ marginTop: 12, fontSize: 13, color: '#b91c1c' }}>
            {kwMsg}
          </div>
        )}
      </CollapsibleCard>

      {/* Watchlist */}
      <WatchlistEditor
        watchedGroups={watchedGroups}
        clientAccountId={clientAccountId}
        onReload={onReload}
      />

      {/* AI training */}
      <AiTrainingEditor profile={profile} onReload={onReload} />
    </>
  );
}

// -----------------------------------------------------------------------------
// WatchlistEditor — the Facebook groups + Reddit subreddits being monitored.
// Add by URL, toggle enabled, remove. Writes to /api/me/config/watched-groups,
// which is the same watched_groups table the extension reads.
// -----------------------------------------------------------------------------
function WatchlistEditor({
  watchedGroups,
  clientAccountId,
  onReload,
}: {
  watchedGroups: MeConfigResponse['watched_groups'];
  clientAccountId: string;
  onReload: () => Promise<void>;
}) {
  const { request } = useApi();
  const [newUrl, setNewUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Detect platform from the pasted URL so the user doesn't pick it manually.
  function detectPlatform(url: string): 'facebook' | 'reddit' | null {
    if (/facebook\.com\/groups\//i.test(url)) return 'facebook';
    if (
      /reddit\.com\/r\/[A-Za-z0-9_]{3,21}/i.test(url) ||
      /^\/?r\/[A-Za-z0-9_]{3,21}\/?$/i.test(url)
    )
      return 'reddit';
    return null;
  }

  async function addGroup() {
    const url = newUrl.trim();
    if (!url) return;
    const platform = detectPlatform(url);
    if (!platform) {
      setMsg(
        'Paste a facebook.com/groups/… URL or a reddit.com/r/… URL.',
      );
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const res = await request<{ already_existed?: boolean }>(
        '/api/me/config/watched-groups',
        {
          method: 'POST',
          body: JSON.stringify({
            client_account_id: clientAccountId,
            group_url: url,
            platform,
          }),
        },
      );
      setNewUrl('');
      if (res.already_existed) setMsg('That one is already on the watchlist.');
      await onReload();
    } catch (e) {
      setMsg('Add failed: ' + (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleGroup(id: string, enabled: boolean) {
    setBusy(true);
    setMsg(null);
    try {
      await request(`/api/me/config/watched-groups/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
      });
      await onReload();
    } catch (e) {
      setMsg('Update failed: ' + (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function removeGroup(id: string, label: string) {
    if (!window.confirm(`Remove "${label}" from the watchlist?`)) return;
    setBusy(true);
    setMsg(null);
    try {
      await request(`/api/me/config/watched-groups/${id}`, {
        method: 'DELETE',
      });
      await onReload();
    } catch (e) {
      setMsg('Remove failed: ' + (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const facebook = watchedGroups.filter(
    (g) => (g.platform ?? 'facebook') === 'facebook',
  );
  const reddit = watchedGroups.filter((g) => g.platform === 'reddit');

  const row = (g: MeConfigResponse['watched_groups'][number]) => {
    const label = g.group_name || g.group_id;
    return (
      <div key={g.id} className="wl-row">
        <label className="wl-toggle" title={g.enabled ? 'Monitoring' : 'Paused'}>
          <input
            type="checkbox"
            checked={g.enabled === 1}
            disabled={busy}
            onChange={(e) => void toggleGroup(g.id, e.target.checked)}
          />
        </label>
        <div className="wl-main">
          <span className="wl-name">{label}</span>
          {g.group_url && (
            <a
              className="wl-url"
              href={g.group_url}
              target="_blank"
              rel="noreferrer"
            >
              {g.group_url}
            </a>
          )}
        </div>
        <button
          type="button"
          className="wl-remove"
          onClick={() => void removeGroup(g.id, label)}
          disabled={busy}
        >
          Remove
        </button>
      </div>
    );
  };

  return (
    <CollapsibleCard title="Watchlist">
      <p className="muted" style={{ marginBottom: 16 }}>
        The Facebook groups and Reddit subreddits GroupPulse monitors. Paste a
        group or subreddit URL to add one. Toggle the switch to pause a source
        without removing it. Changes sync to the extension within 60s.
      </p>

      <div className="kw-add-row" style={{ marginBottom: 16 }}>
        <input
          type="text"
          value={newUrl}
          placeholder="https://www.facebook.com/groups/…  or  https://www.reddit.com/r/…"
          onChange={(e) => setNewUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void addGroup();
          }}
          disabled={busy}
        />
        <button
          className="btn btn-primary"
          onClick={() => void addGroup()}
          disabled={busy || !newUrl.trim()}
        >
          Add
        </button>
      </div>

      {watchedGroups.length === 0 ? (
        <p className="muted" style={{ fontStyle: 'italic', margin: 0 }}>
          Nothing on the watchlist yet. Add a Facebook group or Reddit
          subreddit above to start capturing leads from it.
        </p>
      ) : (
        <div className="wl-groups">
          {facebook.length > 0 && (
            <>
              <div className="wl-section-label">
                Facebook groups <span className="kw-count">{facebook.length}</span>
              </div>
              {facebook.map(row)}
            </>
          )}
          {reddit.length > 0 && (
            <>
              <div
                className="wl-section-label"
                style={{ marginTop: facebook.length > 0 ? 14 : 0 }}
              >
                Reddit subreddits <span className="kw-count">{reddit.length}</span>
              </div>
              {reddit.map(row)}
            </>
          )}
        </div>
      )}
      {msg && (
        <div style={{ marginTop: 12, fontSize: 13, color: '#b45309' }}>{msg}</div>
      )}
    </CollapsibleCard>
  );
}

// -----------------------------------------------------------------------------
// AiTrainingEditor — voice, style descriptors, reply examples, custom modes.
// Stored as a JSON blob in profiles.ai_training_json; saved via the existing
// PUT /api/me/config/profile/:id endpoint (which accepts ai_training_json).
// -----------------------------------------------------------------------------
type AiTraining = {
  voiceDescription: string;
  styleChips: string[];
  examples: { post: string; reply: string }[];
  customModes: { name: string; instructions: string }[];
};

function emptyTraining(): AiTraining {
  return { voiceDescription: '', styleChips: [], examples: [], customModes: [] };
}

function parseTraining(json: string | null): AiTraining {
  if (!json) return emptyTraining();
  try {
    const p = JSON.parse(json) as Partial<AiTraining>;
    return {
      voiceDescription:
        typeof p.voiceDescription === 'string' ? p.voiceDescription : '',
      styleChips: Array.isArray(p.styleChips)
        ? p.styleChips.filter((x): x is string => typeof x === 'string')
        : [],
      examples: Array.isArray(p.examples)
        ? p.examples
            .filter((e): e is { post: string; reply: string } =>
              !!e && typeof e === 'object',
            )
            .map((e) => ({ post: e.post ?? '', reply: e.reply ?? '' }))
        : [],
      customModes: Array.isArray(p.customModes)
        ? p.customModes
            .filter((m): m is { name: string; instructions: string } =>
              !!m && typeof m === 'object',
            )
            .map((m) => ({ name: m.name ?? '', instructions: m.instructions ?? '' }))
        : [],
    };
  } catch {
    return emptyTraining();
  }
}

function AiTrainingEditor({
  profile,
  onReload,
}: {
  profile: ConfigProfile;
  onReload: () => Promise<void>;
}) {
  const { request } = useApi();
  const [training, setTraining] = useState<AiTraining>(() =>
    parseTraining(profile.ai_training_json),
  );
  const [chipText, setChipText] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Re-seed when switching to a different profile (owner picked another
  // client). Keyed on profile.id so a config refetch doesn't wipe edits.
  useEffect(() => {
    setTraining(parseTraining(profile.ai_training_json));
    setChipText('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id]);

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      await request(`/api/me/config/profile/${profile.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ai_training_json: JSON.stringify(training) }),
      });
      setMsg('Saved ✓ — syncing to the extension within 60s.');
      window.setTimeout(() => setMsg(null), 4000);
      await onReload();
    } catch (e) {
      setMsg('Save failed: ' + (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function addChip() {
    const v = chipText.trim();
    if (!v) return;
    if (!training.styleChips.includes(v)) {
      setTraining((t) => ({ ...t, styleChips: [...t.styleChips, v] }));
    }
    setChipText('');
  }

  return (
    <CollapsibleCard title="AI Training">
      <p className="muted" style={{ marginBottom: 16 }}>
        Teach the AI your voice. This shapes the suggested replies on the Leads
        page and in the extension.
      </p>

      <div className="profile-form">
        <ProfileField
          label="Voice description"
          value={training.voiceDescription}
          onChange={(v) => setTraining((t) => ({ ...t, voiceDescription: v }))}
          textarea
          placeholder="e.g. Warm and direct. Talks like a helpful peer, never salesy. Short sentences."
        />

        <label className="profile-field">
          <span className="profile-field-label">Style descriptors</span>
          <div className="kw-add-row">
            <input
              type="text"
              value={chipText}
              placeholder="e.g. concise, friendly, no jargon"
              onChange={(e) => setChipText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addChip();
                }
              }}
            />
            <button
              className="btn btn-secondary"
              type="button"
              onClick={addChip}
              disabled={!chipText.trim()}
            >
              Add
            </button>
          </div>
          <div className="kw-chips" style={{ marginTop: 8 }}>
            {training.styleChips.length === 0 ? (
              <span className="muted" style={{ fontSize: 13, fontStyle: 'italic' }}>
                No style descriptors yet.
              </span>
            ) : (
              training.styleChips.map((chip, i) => (
                <span key={i} className="kw-chip kw-chip-pos">
                  {chip}
                  <button
                    type="button"
                    aria-label={`Remove ${chip}`}
                    onClick={() =>
                      setTraining((t) => ({
                        ...t,
                        styleChips: t.styleChips.filter((_, idx) => idx !== i),
                      }))
                    }
                  >
                    ✕
                  </button>
                </span>
              ))
            )}
          </div>
        </label>
      </div>

      {/* Reply examples */}
      <div style={{ marginTop: 18 }}>
        <div className="wl-section-label">
          Reply examples
          <span className="kw-count">{training.examples.length}</span>
        </div>
        <p className="muted" style={{ fontSize: 13, margin: '4px 0 10px' }}>
          Show the AI a few post → ideal-reply pairs. It mirrors this voice.
        </p>
        {training.examples.map((ex, i) => (
          <div key={i} className="ai-example">
            <textarea
              value={ex.post}
              placeholder="Example post…"
              rows={2}
              onChange={(e) =>
                setTraining((t) => ({
                  ...t,
                  examples: t.examples.map((x, idx) =>
                    idx === i ? { ...x, post: e.target.value } : x,
                  ),
                }))
              }
            />
            <textarea
              value={ex.reply}
              placeholder="Ideal reply…"
              rows={2}
              onChange={(e) =>
                setTraining((t) => ({
                  ...t,
                  examples: t.examples.map((x, idx) =>
                    idx === i ? { ...x, reply: e.target.value } : x,
                  ),
                }))
              }
            />
            <button
              type="button"
              className="wl-remove"
              onClick={() =>
                setTraining((t) => ({
                  ...t,
                  examples: t.examples.filter((_, idx) => idx !== i),
                }))
              }
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-secondary"
          style={{ fontSize: 13, padding: '6px 12px', marginTop: 8 }}
          onClick={() =>
            setTraining((t) => ({
              ...t,
              examples: [...t.examples, { post: '', reply: '' }],
            }))
          }
        >
          + Add example
        </button>
      </div>

      {/* Custom modes */}
      <div style={{ marginTop: 18 }}>
        <div className="wl-section-label">
          Custom reply modes
          <span className="kw-count">{training.customModes.length}</span>
        </div>
        <p className="muted" style={{ fontSize: 13, margin: '4px 0 10px' }}>
          Named reply styles beyond the built-in value-add / promotional ones.
        </p>
        {training.customModes.map((m, i) => (
          <div key={i} className="ai-example">
            <input
              type="text"
              value={m.name}
              placeholder="Mode name — e.g. Quick question"
              onChange={(e) =>
                setTraining((t) => ({
                  ...t,
                  customModes: t.customModes.map((x, idx) =>
                    idx === i ? { ...x, name: e.target.value } : x,
                  ),
                }))
              }
            />
            <textarea
              value={m.instructions}
              placeholder="Instructions for this mode…"
              rows={2}
              onChange={(e) =>
                setTraining((t) => ({
                  ...t,
                  customModes: t.customModes.map((x, idx) =>
                    idx === i ? { ...x, instructions: e.target.value } : x,
                  ),
                }))
              }
            />
            <button
              type="button"
              className="wl-remove"
              onClick={() =>
                setTraining((t) => ({
                  ...t,
                  customModes: t.customModes.filter((_, idx) => idx !== i),
                }))
              }
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-secondary"
          style={{ fontSize: 13, padding: '6px 12px', marginTop: 8 }}
          onClick={() =>
            setTraining((t) => ({
              ...t,
              customModes: [...t.customModes, { name: '', instructions: '' }],
            }))
          }
        >
          + Add mode
        </button>
      </div>

      <div className="actions" style={{ justifyContent: 'flex-start', marginTop: 18 }}>
        <button
          className="btn btn-primary"
          onClick={() => void save()}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save AI Training'}
        </button>
        {msg && (
          <span
            style={{
              fontSize: 13,
              color: msg.startsWith('Save failed') ? '#b91c1c' : '#15803d',
              alignSelf: 'center',
            }}
          >
            {msg}
          </span>
        )}
      </div>
    </CollapsibleCard>
  );
}
